import React, { useState } from 'react';

import { executeScript } from '../services/utilitiesService';
import { user } from '../state/user';
import { UtilCommand } from '../Types/Util';

const Utility = ({ data }: { data: UtilCommand }) => {
    const [message, setMessage] = useState<string>('');
    const [args, setArgs] = useState<object>({});
    const [isError, setIsError] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Check if user's role is in the allowed roles
    // Access user.value.role directly in the expression to ensure reactivity
    const isRoleAllowed = data.roles.includes(user.value.role || '');

    // Helper function to capitalize labels
    const capitalizeLabel = (str: string): string => {
        return str
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const renderArgDesc = (args: object) => {
        const argKeys = Object.keys(args);

        if (argKeys.length === 0) {
            return (
                <div className="text-sm text-gray-500 italic py-2">
                    No arguments required
                </div>
            );
        }

        return (
            <div className="space-y-4">
                {argKeys.map((k, index) => {
                    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                    const argConfig: any = args[k as keyof typeof args];

                    // Handle both old format (string/array) and new format (object with type and description)
                    const argType = argConfig?.type || 'Text';
                    const argDescription = argConfig?.description || argConfig;
                    const displayValue = Array.isArray(argDescription)
                        ? `[${argDescription.join(', ')}]`
                        : typeof argDescription === 'string'
                            ? argDescription
                            : '';

                    return (
                        <div key={k} className="space-y-2">
                            <label
                                htmlFor={k}
                                className="block text-sm font-medium text-gray-700"
                            >
                                <span className="inline-flex items-center">
                                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-semibold mr-2">
                                        {index + 1}
                                    </span>
                                    {capitalizeLabel(k)}
                                </span>
                            </label>

                            {/* Render input based on type */}
                            {argType === 'TextArea' ? (
                                <textarea
                                    id={k}
                                    name={k}
                                    rows={4}
                                    className="block w-full px-4 py-2.5 text-gray-900 border border-gray-300 rounded-lg
                                             focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                                             transition duration-150 ease-in-out
                                             placeholder:text-gray-400 resize-y"
                                    placeholder={displayValue}
                                    onChange={handleTextAreaChange}
                                />
                            ) : argType === 'FileInput' ? (
                                <div className="space-y-2">
                                    <input
                                        id={k}
                                        name={k}
                                        type="file"
                                        className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg
                                                 cursor-pointer bg-gray-50
                                                 focus:outline-none focus:ring-2 focus:ring-blue-500
                                                 file:mr-4 file:py-2.5 file:px-4
                                                 file:rounded-l-lg file:border-0
                                                 file:text-sm file:font-semibold
                                                 file:bg-blue-50 file:text-blue-700
                                                 hover:file:bg-blue-100
                                                 transition duration-150 ease-in-out"
                                        onChange={handleFileChange}
                                    />
                                </div>
                            ) : (
                                <input
                                    id={k}
                                    name={k}
                                    type="text"
                                    className="block w-full px-4 py-2.5 text-gray-900 border border-gray-300 rounded-lg
                                             focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                                             transition duration-150 ease-in-out
                                             placeholder:text-gray-400"
                                    placeholder={displayValue}
                                    onChange={handleChange}
                                />
                            )}

                            <p className="text-xs text-gray-500 ml-1">
                                {displayValue}
                            </p>
                        </div>
                    );
                })}
            </div>
        );
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        e.preventDefault();
        setArgs({ ...args, [e.target.name]: e.target.value });
    };

    const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        e.preventDefault();
        setArgs({ ...args, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        e.preventDefault();
        const file = e.target.files?.[0];
        if (file) {
            // Store the actual File object for upload
            setArgs({ ...args, [e.target.name]: file });
        }
    };

    async function executeHandler(
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ): Promise<void> {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');

        try {
            const msg: { message: string } = await executeScript(
                data.endpoint,
                args,
                data.http_method,
            );
            setMessage(msg.message);
            setIsError(false);
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        } catch (err: any) {
            setMessage(err.message || 'An error occurred while executing the script');
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-5 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {data.name}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                    {data.description}
                </p>
            </div>

            {/* Content Section */}
            <div className="px-6 py-5 space-y-6">
                {/* Metadata */}
                <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Method:
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {data.http_method || 'POST'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Allowed Roles:
                        </span>
                        <div className="flex gap-1.5">
                            {data.roles.map((role) => (
                                <span
                                    key={role}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                                >
                                    {role}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Arguments Section */}
                <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">
                        Arguments
                    </h4>
                    {renderArgDesc(data.arguments)}
                </div>

                {/* Message Display */}
                {message && (
                    <div
                        className={`rounded-lg p-4 ${
                            isError
                                ? 'bg-red-50 border border-red-200'
                                : 'bg-green-50 border border-green-200'
                        }`}
                    >
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                {isError ? (
                                    <svg
                                        className="h-5 w-5 text-red-400"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                        className="h-5 w-5 text-green-400"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                )}
                            </div>
                            <div className="ml-3 flex-1">
                                <p
                                    className={`text-sm font-medium ${
                                        isError ? 'text-red-800' : 'text-green-800'
                                    }`}
                                >
                                    {isError ? 'Error' : 'Success'}
                                </p>
                                <p
                                    className={`mt-1 text-sm ${
                                        isError ? 'text-red-700' : 'text-green-700'
                                    }`}
                                >
                                    {message}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer with Execute Button */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                {!isRoleAllowed && (
                    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start">
                            <svg
                                className="h-5 w-5 text-amber-400 mt-0.5"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <div className="ml-3">
                                <p className="text-sm font-medium text-amber-800">
                                    Insufficient Permissions
                                </p>
                                <p className="text-sm text-amber-700 mt-1">
                                    Your role "{user.value.role || 'unknown'}" does not have permission to execute this
                                    script. Required roles: {data.roles.join(', ')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                <div className="flex justify-end">
                    <button
                        onClick={executeHandler}
                        disabled={isLoading || !isRoleAllowed}
                        className={`
                            inline-flex items-center px-6 py-2.5 border border-transparent
                            text-sm font-medium rounded-lg shadow-sm text-white
                            transition-all duration-150 ease-in-out
                            ${
                                isLoading || !isRoleAllowed
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 active:bg-blue-800'
                            }
                        `}
                    >
                        {isLoading ? (
                            <>
                                <svg
                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                Executing...
                            </>
                        ) : (
                            <>
                                <svg
                                    className="-ml-1 mr-2 h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M13 10V3L4 14h7v7l9-11h-7z"
                                    />
                                </svg>
                                Execute Script
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Utility;
